import { Group, Path, Point, util } from "fabric";
import planRest from "../../rest/planRest";
import { Vector3D } from "../lod2";
import calcAreaUtils from "./calcAreaUtils";
import { theme } from "../../react/components/GlobalStyle";

const addPlan = async (index, plan, layer, plot, hasControls = true) => {
    const imageBase64 = plan.image || await planRest.getImageById(plan.id);
    const scale = plan.metersPerPoint / plot.refScale;
    const x1 = (0 - plan.referencePointX) * scale;
    const y1 = (0 - plan.referencePointY) * scale;
    const { x, y } = plot.rotatePoint(x1, y1, 0, 0, plan.rotationAngle);
    const imgProps = { index, hasControls };
    const image = await plot.addImage(layer, x, y, plan.rotationAngle, scale, (plan.opacity / 10), imageBase64, imgProps);
    ["bl", "br", "mb", "ml", "mr", "mt", "tl", "tr"]
        .forEach(prop => image.setControlVisible(prop, false));
    return image;
};

const addCalcAreas = (calcAreas, plot, layer, { evented = false, selectable = false, hasControls = false, selectedIndex = undefined } = {}) => {
    layer.removeObjects();
    calcAreas.filter(ca => ca.name !== "OVERVIEW").forEach((calcArea, index) => {
        const groupObjects = [];
        const { x, y, width, height, angle } = calcAreaUtils.reverseCalculateArea(calcArea, plot);
        const { angleCounterclockwiseRadians } = calcAreaUtils.getCalcAreaBaseProperties2D(calcArea, plot);
        const isVertical = calcAreaUtils.isVertical(calcArea);
        const rectHeight = isVertical ? 0.1 : height;
        const fill = height !== 0 ? "transparent" : theme.primaryLight;

        const rect = plot.addRect(layer, 0, 0, width, rectHeight, true, false, {
            originY: "bottom",
            evented,
            fill,
            stroke: theme.primaryLight,
            strokeUniform: true,
            mxwellName: "areaRect",
        });
        groupObjects.push(rect);

        const triangleWidth = width / 8;
        const triangleHeight = isVertical ? -triangleWidth * 0.2 : triangleWidth * 0.2;
        const triangleY = 0;
        const triangle = plot.addTriangle(layer, width / 2 - triangleWidth / 2, triangleY, triangleWidth, triangleHeight, true, false, {
            originY: "bottom",
            evented,
            fill,
            stroke: theme.primaryLight,
            strokeUniform: true,
        });
        groupObjects.push(triangle);

        const { boundary } = calcArea;
        if (!isVertical && boundary) {
            const scale = plot.refScale;
            const scaledBoundary = boundary.map(path => path.map(p => {
                const rotated = new Vector3D(p.x - calcArea.originX, p.y - calcArea.originY, 0)
                    .rotateAroundZ(-angleCounterclockwiseRadians);
                return { x: rotated.x / scale, y: -rotated.y / scale };
            }));
            const pathData = plot.createSvgPath(scaledBoundary);
            const path = new Path(pathData, {
                evented,
                stroke: theme.black,
                strokeUniform: true,
                strokeWidth: 10,
                fill: "transparent",
                mxwellName: "areaBoundary",
            });
            path.originalTransformMatrix = path.calcTransformMatrix();
            groupObjects.push(path);
        }

        const groupCorrection = new Vector3D(0, triangleHeight, 0).rotateAroundZ(angleCounterclockwiseRadians);
        const top = isVertical ? y - groupCorrection.y : y;
        const left = isVertical ? x + groupCorrection.x : x;

        const group = new Group(groupObjects, {
            index,
            sourceArea: calcArea,
            mxwellName: "areaGroup",
            top,
            left,
            angle,
            evented,
            originX: "left",
            originY: "bottom",
            selectable,
            hasControls,
        });
        if (isVertical) {
            ["mb", "ml", "mr", "mt", "bl", "br"]
                .forEach(prop => group.setControlVisible(prop, false));
        }
        layer.addObject(group);
        if (selectedIndex === index) {
            plot.canvas.setActiveObject(group);
        }
    });
    plot.canvas.renderAll();
};

const refreshAfterZoom = async plot => {
    plot.canvas.fire("rescale");
    plot.canvas.fire("viewport");
    plot.canvas.calcOffset();
    plot.canvas.requestRenderAll();
    await new Promise(resolve => requestAnimationFrame(resolve));
    plot.canvas.requestRenderAll();
};

const zoomToObjects = (plot, objects, padding = 0) => {
    if (!objects || objects.length === 0) {return;}
    const { canvas } = plot;
    canvas.setZoom(1);
    const viewportBoundaries = canvas.calcViewportBoundaries();
    const vpHeight = viewportBoundaries.bl.y - viewportBoundaries.tl.y;
    const vpWidth = viewportBoundaries.br.x - viewportBoundaries.bl.x;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const obj of objects) {
        const bounds = obj.getBoundingRect();
        minX = Math.min(minX, bounds.left);
        minY = Math.min(minY, bounds.top);
        maxX = Math.max(maxX, bounds.left + bounds.width);
        maxY = Math.max(maxY, bounds.top + bounds.height);
    }
    const totalWidth = maxX - minX;
    const totalHeight = maxY - minY;
    const ratioX = (totalWidth + padding) / vpWidth;
    const ratioY = (totalHeight + padding) / vpHeight;
    const ratio = Math.max(ratioX, ratioY);
    const zoom = 1 / ratio;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    canvas.setZoom(zoom);
    canvas.absolutePan(new Point(centerX * zoom - vpWidth / 2, centerY * zoom - vpHeight / 2));
};

const zoomToObject = (plot, object, padding = 0) => {
    const { canvas } = plot;
    canvas.setZoom(1);
    const viewportBoundaries = canvas.calcViewportBoundaries();
    const vpHeight = viewportBoundaries.bl.y - viewportBoundaries.tl.y;
    const vpWidth = viewportBoundaries.br.x - viewportBoundaries.bl.x;
    const bounds = object.getBoundingRect();
    const ratioX = (bounds.width + padding) / vpWidth;
    const ratioY = (bounds.height + padding) / vpHeight;
    const ratio = Math.max(ratioX, ratioY);
    const zoom = 1 / ratio;
    const objCenter = object.getCenterPoint();
    canvas.setZoom(zoom);
    canvas.absolutePan(new Point(objCenter.x * zoom - vpWidth / 2, objCenter.y * zoom - vpHeight / 2));
};

const generatePngUrl = plot => plot.canvas.getElement().toDataURL({
    format: "image/png",
    quality: 0.8
});

const calculteAreaSidesClickRatio = opt => {
    const { target } = opt;
    const pointer = opt.absolutePointer || opt.scenePoint;
    const matrix = target.calcTransformMatrix();
    const invertedMatrix = util.invertTransform(matrix);
    const localPoint = util.transformPoint(pointer, invertedMatrix);
    const ratioU = (localPoint.x + target.width / 2) / target.width;
    const ratioV = (-localPoint.y + target.height / 2) / target.height;
    return { ratioU, ratioV };
};

// ray casting algorithm (even-odd rule)
const isPointInsideBoundary = (point, boundary) => {
    if (!boundary || boundary.length === 0) {
        return false;
    }

    const { x, y } = point;

    let isInside = false;

    // iterate through each polygon
    for (const polygon of boundary) {
        if (!polygon || polygon.length < 3) {
            continue;
        }

        // iterate through each edge of the polygon
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const pi = polygon[i];
            const pj = polygon[j];

            // ray casting algorithm using even odd rule
            // check if the point is within the y range of the edge and if the ray intersects the edge
            const intersect = ((pi.y > y) !== (pj.y > y)) &&
                (x < (pj.x - pi.x) * (y - pi.y) / (pj.y - pi.y) + pi.x);

            if (intersect) {
                isInside = !isInside;
            }
        }
    }

    return isInside;
};

const isPointerWithiImageClipPath = (opt, plot) => {
    const image = opt.target;
    if (image?.clipPath?.path) {
        const boundaryPolygons = plot.pathFromSvg(image.clipPath);
        if (!isPointInsideBoundary(plot.canvas.getPointer(opt.e, false), boundaryPolygons)) {
            return false;
        }
    }
    return true;
};

const plotUtils = { addPlan, addCalcAreas, zoomToObject, zoomToObjects, generatePngUrl, isPointerWithiImageClipPath, calculteAreaSidesClickRatio, refreshAfterZoom };

export default plotUtils;
